import React, { useContext, useState, useEffect } from 'react'
import { decodeToken, isExpired } from 'react-jwt'
import AuthContext from '../contexts/AuthContext'

type AuthenticationData = {
  authToken: string
  initializing: boolean
  authUser: {
    id: string
    role: string
    department_id: string
    email: string
    first_name: string
    last_name: string
    majlis: string
    created_at?: string
    exp?: number
    last_signed_in?: string
    role_id: number
    site_admin: boolean
  }
}
type AuthContextType = {
  authUser: AuthenticationData['authUser']
  authToken: AuthenticationData['authToken']
  initializing: AuthenticationData['initializing']
  signIn?: (token: string) => void
  signOut?: () => void
}

const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(
    localStorage.getItem('jwtToken') || ''
  )
  const [authState, setAuthState] = useState<AuthContextType>({
    authUser: {
      id: '',
      role: '',
      department_id: '',
      email: '',
      first_name: '',
      last_name: '',
      majlis: '',
      role_id: 0,
      site_admin: false,
    },
    authToken: '',
    initializing: true,
  })

  const signIn = (token) => {
    localStorage.setItem('jwtToken', token)
    setAuthToken(token)
  }

  const signOut = () => {
    localStorage.removeItem('jwtToken')
    setAuthState({
      authUser: {
        id: '',
        role: '',
        department_id: '',
        email: '',
        first_name: '',
        last_name: '',
        majlis: '',
        role_id: 0,
        site_admin: false,
      },
      authToken: '',
      initializing: false,
    })
  }

  useEffect(() => {
    const authUser: any = decodeToken(authToken)
    if (authUser) {
      delete authUser.password
    }
    const isAuthTokenExpired = isExpired(authToken)
    if (isAuthTokenExpired) {
      signOut()
      return
    }
    setAuthState({ authUser, authToken, initializing: false })
  }, [authToken])

  return (
    <AuthContext.Provider value={{ authState, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthentication = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthProvider
